/**
 * Store Error Reporting Utilities
 * Provides error handling functionality for Pinia stores without Vue composables
 *
 * IMPORTANT: These utilities are specifically designed for use in Pinia store actions.
 * They do NOT use Vue composables (inject/provide) and therefore work outside component context.
 *
 * For component usage, use @/composables/useErrorReporting instead.
 *
 * Three types of errors:
 * 1. Fatal - Shows Error.vue page and stops execution (e.g., auth failure, critical API failure)
 * 2. Non-fatal - Shows ErrorBanner at top of page (e.g., form submission error, data load error)
 * 3. Silent - Only logs to console (e.g., background sync failure, analytics error)
 */

import { useAppStore } from '@/stores/useAppStore';
import { reportErrorSpan } from '@/honeycomb';

/**
 * Parse error response to extract useful information
 * Handles different error response formats from API
 *
 * @param {Error} error - The error object
 * @returns {Object} Parsed error details
 */
function parseErrorDetails(error) {
    const meta = error?.response?.data?.meta;

    return {
        message: meta?.message || error?.response?.data?.message || error.message || 'An unexpected error occurred',
        exceptionType: meta?.exception_type,
        problemId: meta?.problem_identifier,
        requestId: meta?.request_id,
    };
}

/**
 * Build error message text for banner display
 * Formats error details into a user-friendly string
 *
 * @param {Object} details - Parsed error details
 * @returns {string} Formatted error message
 */
function buildErrorMessage(details) {
    const parts = [details.message];

    if (details.problemId) {
        parts.push(`Problem ID: ${details.problemId}`);
    }

    if (details.requestId) {
        parts.push(`Request ID: ${details.requestId}`);
    }

    return parts.join(' | ');
}

/**
 * Convert value to Error object
 * Ensures we always work with Error instances
 *
 * @param {*} value - Value to convert (string, Error, or other)
 * @returns {Error} Error object
 */
function getError(value) {
    return value instanceof Error ? value : new Error(value);
}

/**
 * Report error by logging it with a kind label
 * This allows error tracking services to capture it
 *
 * @param {Error} error - Error to report
 * @param {string} kind - Error kind ('fatal', 'non-fatal', 'silent')
 */
function reportError(error, kind) {
    reportErrorSpan(error, kind);

    // Log error asynchronously so error tracking services can capture it
    // setTimeout ensures it doesn't interfere with current execution
    setTimeout(() => {
        console.error(`[Error: ${kind}]`, error.message, error);
    }, 1);
}

/**
 * Report a fatal error
 * Shows Error.vue page via appStore.updateError()
 * Use for: authentication failures, critical API errors, missing required data
 *
 * @param {Error|string} value - Error value
 * @param {Object} options - Additional error options
 * @param {string} options.title - Error title for Error.vue
 * @param {string} options.message - Error message for Error.vue
 * @param {Object} options.primaryButton - Optional button config
 * @param {string} options.image - Optional image name (discoSad, meiStressed, zoeConcerned)
 */
export function reportFatalError(value, options = {}) {
    const error = getError(value);
    reportError(error, 'fatal');

    const details = parseErrorDetails(error);
    const appStore = useAppStore();

    appStore.updateError({
        ...options,
        title: options.title || 'Something went wrong',
        message: options.message || details.message,
    });

    // Note: We can't call router.push() here since we don't have router access
    // The route guard in authGuard.js will handle navigation to error page
}

/**
 * Report a non-fatal error
 * Shows ErrorBanner at top of app via appStore.setBannerError()
 * Use for: form validation errors, API failures where user can retry, data load errors
 *
 * @param {Error|string} value - Error value
 * @param {Object} options - Additional error options
 * @param {string} options.title - Error title
 * @param {string} options.color - Banner color (defaults to 'error')
 */
export function reportNonFatalError(value, options = {}) {
    const error = getError(value);
    reportError(error, 'non-fatal');

    const details = parseErrorDetails(error);
    const message = buildErrorMessage(details);
    const appStore = useAppStore();

    const title = options.title || 'There was a problem. Please try again.';

    appStore.setBannerError({
        title,
        message,
        color: options.color || 'error',
    });
}

/**
 * Report a silent error
 * Only logs to console, no UI change
 * Use for: background sync failures, analytics errors, non-critical issues
 *
 * @param {Error|string} value - Error value
 */
export function reportSilentError(value) {
    const error = getError(value);
    reportError(error, 'silent');
}

/**
 * Clear banner error
 * Call this when user dismisses the ErrorBanner
 */
export function clearBannerError() {
    const appStore = useAppStore();
    appStore.clearBannerError();
}
